// Import all CSS - Vite will bundle it and the css-injected-by-js plugin will make it available
import '../../../entrypoints/styles.css'

import React from 'react'
import ReactDOM from 'react-dom'

import { VoiceAgentWithRoom } from './VoiceAgentWithRoom'

/**
 * Embeddable Voice Agent Widget
 *
 * This file provides the entry point for embedding the Resemble Voice Agent
 * into third-party websites. It exposes a global API on window.ResembleVoiceAgent
 *
 * Usage:
 * ```html
 * <script src="https://app.resemble.ai/embed/resemble-voice-agent.js"></script>
 * <script>
 *   ResembleVoiceAgent.init({
 *     embedKey: 'pk_agent_...',
 *     agentId: 'agent-uuid',
 *     apiBaseUrl: 'https://app.resemble.ai'
 *   });
 * </script>
 * ```
 *
 * Note: brandName is hardcoded to "Resemble AI" and cannot be customized
 */

interface EmbedConfig {
  embedKey: string
  agentId: string
  // Optional configuration
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  apiBaseUrl?: string
  autoConnect?: boolean // Auto-connect on mount (default: false, shows idle state first)
  primaryColor?: string // Primary accent color for the widget (default: '#4ade80')
}

interface WidgetInstance {
  destroy: () => void
  open: () => void
  close: () => void
}

class ResembleVoiceAgentSDK {
  private container: HTMLDivElement | null = null
  private shadowRoot: ShadowRoot | null = null
  private shadowContainer: HTMLDivElement | null = null
  private config: EmbedConfig | null = null

  /**
   * Initialize the voice agent widget
   */
  init(config: EmbedConfig): WidgetInstance {
    if (!config.embedKey) {
      throw new Error('ResembleVoiceAgent: embedKey is required')
    }

    if (!config.agentId) {
      throw new Error('ResembleVoiceAgent: agentId is required')
    }

    this.config = config

    // Create container element
    this.container = document.createElement('div')
    this.container.id = 'resemble-voice-agent-container'

    // Set position styles on container
    const position = config.position || 'bottom-right'
    this.container.style.position = 'fixed'
    this.container.style.zIndex = '999999'

    // Position the widget
    switch (position) {
      case 'bottom-right':
        this.container.style.bottom = '20px'
        this.container.style.right = '20px'
        break
      case 'bottom-left':
        this.container.style.bottom = '20px'
        this.container.style.left = '20px'
        break
      case 'top-right':
        this.container.style.top = '20px'
        this.container.style.right = '20px'
        break
      case 'top-left':
        this.container.style.top = '20px'
        this.container.style.left = '20px'
        break
    }

    // Attach shadow DOM for style isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'open' })

    // Create a style container and React root container in shadow DOM
    const shadowContainer = document.createElement('div')
    shadowContainer.id = 'resemble-voice-agent-root'
    this.shadowRoot.appendChild(shadowContainer)

    // Inject all bundled CSS into Shadow DOM
    // The vite-plugin-css-injected-by-js will have already injected CSS into the document
    // We need to find it and copy it into the Shadow DOM
    let bundledCSS = ''

    // The plugin creates a style tag - find it by looking for one with Tailwind CSS
    const styleTags = Array.from(document.head.querySelectorAll('style'))
    const cssStyleTag = styleTags.find((style) => style.textContent && style.textContent.includes('.tw-'))

    if (cssStyleTag) {
      bundledCSS = cssStyleTag.textContent || ''
      // Remove from document head since we're using Shadow DOM for isolation
      cssStyleTag.remove()
    }

    const styleSheet = document.createElement('style')
    styleSheet.textContent = `
      /* Reset and base styles for Shadow DOM isolation */
      * {
        box-sizing: border-box;
      }

      #resemble-voice-agent-root {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }

      /* Bundled application styles (Tailwind + custom CSS) */
      ${bundledCSS}
    `
    this.shadowRoot.appendChild(styleSheet)

    // Store reference to shadow container for cleanup
    this.shadowContainer = shadowContainer

    // Mount React app (React 17 API)
    ReactDOM.render(
      <React.StrictMode>
        <VoiceAgentWithRoom
          agentId={config.agentId}
          authMode='external'
          apiKey={config.embedKey}
          apiBaseUrl={config.apiBaseUrl}
          autoConnect={config.autoConnect ?? false} // Default to false for embeds (show idle state)
          position={config.position}
          brandName='Resemble AI'
          primaryColor={config.primaryColor}
        />
      </React.StrictMode>,
      shadowContainer,
    )

    // Append to document body
    document.body.appendChild(this.container)

    // Return instance with control methods
    return {
      destroy: () => this.destroy(),
      open: () => this.open(),
      close: () => this.close(),
    }
  }

  /**
   * Destroy the widget and clean up
   */
  private destroy() {
    // Unmount React app (React 17 API)
    if (this.shadowContainer) {
      ReactDOM.unmountComponentAtNode(this.shadowContainer)
      this.shadowContainer = null
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }

    this.container = null
    this.shadowRoot = null
    this.config = null
  }

  /**
   * Open/show the widget
   */
  private open() {
    if (this.container) {
      this.container.style.display = 'block'
    }
  }

  /**
   * Close/hide the widget
   */
  private close() {
    if (this.container) {
      this.container.style.display = 'none'
    }
  }
}

// Export to global window object for third-party usage
declare global {
  interface Window {
    ResembleVoiceAgent: ResembleVoiceAgentSDK
  }
}

// Create singleton instance
const sdk = new ResembleVoiceAgentSDK()

// Expose on window
if (typeof window !== 'undefined') {
  window.ResembleVoiceAgent = sdk
}

// Also export for ES modules (if someone wants to import it)
export default sdk
